import {
	onMounted,
	createApp,
	h,
	defineAsyncComponent,
	reactive,
	type App,
	type UnwrapNestedRefs,
} from 'vue';
import { provideApolloClient } from '@vue/apollo-composable';
import { type QueryOptions, type ApolloClient } from '@apollo/client/core';
import { gql } from 'graphql-tag';
import { createApolloClient } from '@/lib/apollo';
import { refreshGlobalQueries } from '@/lib/apollo/refreshGlobalQueries';
import {
	useSetUpPermissions,
	useCreateSocketConnection,
	useBeforeUnloadEventHandler,
	useTranslation,
} from '@/composables';
import { configureVue } from './configureVue';
import { i18n, loadActiveLanguage, loadCustomTranslations } from '@/lib/i18n';
import { useBookmarksStore } from '@/features/Bookmark';
import {
	projectMicroFrontends,
	useProjectDetailsViewStore,
	useProjectModifyModalStore,
	useProjectModifyViewStore,
} from '@/features/Project';
import { useMessagesStore } from '@/features/Message/stores';
import { usePageUnloadStore } from '@/stores/pageUnload';
import { workReportMicroFrontends } from '@/features/WorkReport';
import { renderMonolithVueComponents } from './registerMonolithVueComponents';
import { captureException } from '@sentry/vue';
import {
	useHelpStationStore,
	helpStationMicroFrontends,
	useVirtualAssistantStore,
} from '@/features/HelpStation';
import { appMicroFrontends } from '@/features/App';
import { integrationsSettingsMicroFrontends } from '@/features/IntegrationsSettings';
import {
	selfServiceMicroFrontends,
	useSelfServiceFormStore,
} from '@/features/SelfService';
import { projectPhaseMicroFrontends } from '@/features/ProjectPhase';
import { retrospectiveLinkingMicroFrontends } from '@/features/RetrospectiveLinking';
import { taskMicroFrontends } from '@/features/Task';
import { priceListMicroFrontends } from '@/features/PriceList';
import {
	userRegistrationMicroFrontends,
	userEventAssignMicroFrontends,
} from '@/features/User';
import {
	useReportsLibraryStore,
	reportsMicroFrontends,
} from '@/features/Reports';
import { shareMicroFrontends } from '@/features/Share';
import { retainerMicroFrontends } from '@/features/Retainer';
import { revenueReportForecastFrontends } from '@/features/RevenueReportForecast';
import { timeOffReportMicroFrontends } from '@/features/TimeOffReport';
import { TimeOffSettingsPageFrontends } from '@/features/TimeOffSetting';
import { timesheetSettingsPageFrontends } from '@/features/TimsheetSettings';
import { mcpServerSettingsFrontends } from '@/features/McpServerSettings';
import { quoteMicroFrontends } from '@/features/Quote';
import { salesforceIntegrationMicroFrontends } from '@/features/SalesforceIntegration';
import { settingsMicroFrontends } from '@/features/Setting';
import { calendarMicroFrontends } from '@/features/Calendar/';
import { contactMicroFrontends } from '@/features/Contact';
import { integrationsMicroFrontends } from '@/features/Integrations';
import {
	resourcePlanningMicroFrontends,
	type useResourcePlanningStore,
} from '@/features/ResourcePlanning';
import { teamReportMicroFrontends } from '@/features/TeamReport';
import { type GanttEnterprise } from '../../../inc/_components/ganttChart/dhtmlxgantt';
import { type Gantt } from '@/types';
import {
	userInvitePopupMicroFrontends,
	useUserInvitePopupStore,
} from '@/features/UserInvitePopup';
import { type LiterallyAnything } from '@/types/utils';
import { useMainStore } from '@/stores/main';
import { useEventPopupStore } from '@/stores/eventPopup';
import { commentMicroFrontends } from '@/features/Comment';
import { billableTimeMicroFrontends } from '@/features/BillableTime';
import { eliMicroFrontends } from '@/features/Eli';
import { emailSettingsMicroFrontends } from '@/features/EmailSettings';
import { subtaskMicroFrontends } from '@/features/Subtask';
import { purchaseOrderMicroFrontends } from '@/features/PurchaseOrder';
import { timeEntryMicroFrontends } from '@/features/TimeEntry';

/**
 * All micro-frontends that can be rendered.
 */
const microFrontends = {
	RouterView: defineAsyncComponent(() => import('./RouterView.vue')),
	...appMicroFrontends,
	...integrationsSettingsMicroFrontends,
	...selfServiceMicroFrontends,
	...workReportMicroFrontends,
	...projectMicroFrontends,
	...projectPhaseMicroFrontends,
	...retrospectiveLinkingMicroFrontends,
	...taskMicroFrontends,
	...priceListMicroFrontends,
	...helpStationMicroFrontends,
	...userRegistrationMicroFrontends,
	...userEventAssignMicroFrontends,
	...reportsMicroFrontends,
	...shareMicroFrontends,
	...retainerMicroFrontends,
	...revenueReportForecastFrontends,
	...timeOffReportMicroFrontends,
	...quoteMicroFrontends,
	...salesforceIntegrationMicroFrontends,
	...userInvitePopupMicroFrontends,
	...settingsMicroFrontends,
	...calendarMicroFrontends,
	...integrationsMicroFrontends,
	...resourcePlanningMicroFrontends,
	...contactMicroFrontends,
	...teamReportMicroFrontends,
	...commentMicroFrontends,
	...billableTimeMicroFrontends,
	...eliMicroFrontends,
	...emailSettingsMicroFrontends,
	...TimeOffSettingsPageFrontends,
	...subtaskMicroFrontends,
	...timesheetSettingsPageFrontends,
	...mcpServerSettingsFrontends,
	...purchaseOrderMicroFrontends,
	...timeEntryMicroFrontends,
};

type ModalOptions = {
	name: keyof typeof microFrontends;
	options?: Record<string, unknown>;
};

type Position = {
	top: number;
	left: number;
};

type PopoverOptions = {
	component: keyof typeof microFrontends;
	elementId: string;
	position: Position;
	options?: Record<string, unknown>;
};

type RenderOptions = {
	/**
	 * The CSS selector for an element that the component will be mounted to.
	 *
	 * @example
	 * render({ element: '#mfe-core', component: 'SomeComponent' });
	 */
	element: string | HTMLElement;
	/**
	 * The component's name that should be rendered to the element.
	 *
	 * This can only be one of the micro-frontend names declared in the
	 * `microFrontends` map above.
	 */
	component: keyof typeof microFrontends;
	/**
	 * Any Vue `createElement` arguments can be passed in here. These options
	 * will be passed to the rendered component.
	 *
	 * See the full reference of possible values here:
	 * https://vuejs.org/v2/guide/render-function.html#The-Data-Object-In-Depth
	 *
	 * @example
	 * render({
	 *   component: 'MyComponent',
	 *   element: '#mfe-my-component',
	 *   options: {
	 *     myProp: 42,
	 *     onClick(event) { console.log(event) },
	 *   },
	 * });
	 */
	options?: Record<string, unknown>;

	isAuthenticated?: boolean;
};

declare global {
	interface Window {
		__MFE_UTILS__: {
			render(opts: RenderOptions): Promise<
				| {
						app: App<Element>;
						options: UnwrapNestedRefs<LiterallyAnything>;
				  }
				| undefined
			>;
			showModal(opts: ModalOptions): void;
			showPopover(opts: PopoverOptions): void;
			renderRouterView(
				opts: Pick<RenderOptions, 'element' | 'options'>
			): void;
			renderMonolithVueComponents: typeof renderMonolithVueComponents;
			pinia: {
				bookmarks: ReturnType<typeof useBookmarksStore>;
				projectModifyView: ReturnType<typeof useProjectModifyViewStore>;
				projectDetailsView: ReturnType<
					typeof useProjectDetailsViewStore
				>;
				projectModal: ReturnType<typeof useProjectModifyModalStore>;
				pageUnload: ReturnType<typeof usePageUnloadStore>;
				helpStation: ReturnType<typeof useHelpStationStore>;
				messages: ReturnType<typeof useMessagesStore>;
				reportsLibrary: ReturnType<typeof useReportsLibraryStore>;
				virtualAssistant: ReturnType<typeof useVirtualAssistantStore>;
				userInvitePopup: ReturnType<typeof useUserInvitePopupStore>;
				main: ReturnType<typeof useMainStore>;
				selfServiceForm: ReturnType<typeof useSelfServiceFormStore>;
				resourcePlanningStore?: ReturnType<
					typeof useResourcePlanningStore
				>;
				eventPopup: ReturnType<typeof useEventPopupStore>;
			};
			apollo: typeof apollo;
			graphQLQuery: typeof graphQLQuery;
			removeBeforeUnloadEventListener: typeof removeBeforeUnloadEventListener;
		};
		tokemon: string;
		ganttChart: Gantt;
		gantt: Gantt;
		Gantt: GanttEnterprise;
	}
}

// force async loading of active language
loadActiveLanguage().then(() => loadCustomTranslations(i18n));

const { removeBeforeUnloadEventListener } = useBeforeUnloadEventHandler();

let apollo: ApolloClient<unknown> | undefined;
let createApolloPromise: Promise<ApolloClient<unknown>>;

export const render = async ({
	element,
	component,
	options = {},
	isAuthenticated = true,
}: RenderOptions): Promise<
	| {
			app: App<Element>;
			options: UnwrapNestedRefs<LiterallyAnything>;
	  }
	| undefined
> => {
	try {
		const comp = microFrontends[component];

		if (createApolloPromise) {
			await createApolloPromise;
		} else if (!apollo) {
			createApolloPromise = createApolloClient(i18n);
			apollo = await createApolloPromise;
			refreshGlobalQueries(apollo);
			window.__MFE_UTILS__.apollo = apollo;
		}

		const optionsReactive = reactive({
			...options,
			class: ['micro-frontend', options.class],
		});

		const app = createApp({
			name: `MFE:${component}`,
			setup() {
				// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
				provideApolloClient(apollo!);
				if (isAuthenticated) {
					useSetUpPermissions();
					useCreateSocketConnection();
				}
				onMounted(async () => {
					// Only render MessagesList in MFE mode, not in SPA mode
					// In SPA mode, MessagesList is already included in App.vue
					const isSpaMode = document.getElementById('app') !== null;
					if (!isSpaMode) {
						const layouts = await import('./layouts');
						layouts.renderMessagesList();
					}
				});

				useMainStore().$patch({
					translation: useTranslation(),
				});

				return () => {
					return h(comp, optionsReactive);
				};
			},
		});

		configureVue(app);

		app.mount(element);

		return { app, options: optionsReactive };
	} catch (error) {
		captureException(error, {
			extra: {
				element,
				component,
				options: JSON.stringify(options),
			},
		});

		return Promise.resolve(undefined);
	}
};

const showModal = async ({ name, options = {} }: ModalOptions) => {
	const elementId = 'sui-modal-placeholder';
	const suiModalPlaceholder = document.createElement('div');
	suiModalPlaceholder.id = elementId;

	document.body.append(suiModalPlaceholder);

	await render({
		element: `#${elementId}`,
		component: name,
		options,
	});
};

const showPopover = async ({
	component,
	elementId,
	position,
	options = {},
}: PopoverOptions) => {
	const popoverPlaceholder = document.createElement('div');

	popoverPlaceholder.id = elementId;
	popoverPlaceholder.style.position = 'absolute';
	popoverPlaceholder.style.top = `${position.top}px`;
	popoverPlaceholder.style.left = `${position.left}px`;

	document.body.append(popoverPlaceholder);

	await render({
		element: `#${elementId}`,
		component,
		options,
	});
};

const renderRouterView = async ({
	element,
	options,
}: Pick<RenderOptions, 'element' | 'options'>) => {
	await render({
		element,
		component: 'RouterView',
		options,
	});
};

/**
 * Allow making GraphQL requests in the monolith through Apollo Client.
 *
 * This is a wrapper function instead of exposing `apollo.client` directly
 * because the `query` parameter needs to be wrapped with the `gql` function,
 * which is not accessible in the monolith JS.
 */
const graphQLQuery = async (
	options: Omit<QueryOptions, 'query'> & {
		query: string;
	}
) => {
	// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
	return apollo!.query({
		...options,
		query: gql(options.query),
	});
};

// it is possible that we initialize the MFE mode from inside SPA mode. In such cases we want to use the already existing stores
const pinia = window.__MFE_UTILS__?.pinia ?? {
	bookmarks: useBookmarksStore(),
	projectModifyView: useProjectModifyViewStore(),
	projectDetailsView: useProjectDetailsViewStore(),
	projectModal: useProjectModifyModalStore(),
	pageUnload: usePageUnloadStore(),
	helpStation: useHelpStationStore(),
	messages: useMessagesStore(),
	reportsLibrary: useReportsLibraryStore(),
	virtualAssistant: useVirtualAssistantStore(),
	userInvitePopup: useUserInvitePopupStore(),
	main: useMainStore(),
	selfServiceForm: useSelfServiceFormStore(),
	eventPopup: useEventPopupStore(),
};

window.__MFE_UTILS__ = {
	render,
	showModal,
	showPopover,
	renderRouterView,
	renderMonolithVueComponents,
	pinia,
	apollo,
	graphQLQuery,
	removeBeforeUnloadEventListener,
};

// Notify monolith JS that all Vue components and Vue itself has been set up.
// Then, the monolith can initialize Vue on its own side.
window.dispatchEvent(new Event('mfe-core-components-loaded'));
